"use client"

import { useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import apiClient from "@/lib/api/api-client"
import { authStorage } from "@/lib/auth/auth-storage"
import { useAuthSession } from "@/lib/auth/auth-provider"
import { authKeys } from "@/lib/hooks/use-auth"
import { usePathnameSafe } from "@/lib/hooks/use-pathname-safe"
import { PageLoading } from "@/components/ui/feedback/page-loading"
import { AgentTermsScreen } from "@/components/auth/agent-terms-screen"
import { PlayerSetupWizard } from "@/components/player-setup/player-setup-wizard"
import type { User } from "@/types/user-types"
import { playerSetupKey } from "@/types/player-setup"
import type { ReactNode } from "react"

const READABLE_WHILE_GATED = new Set(["/terms", "/privacy"])

function normalizePath(pathname: string): string {
  if (!pathname) return "/"
  if (pathname.length > 1 && pathname.endsWith("/")) return pathname.slice(0, -1)
  return pathname
}

function agentMustAccept(user: User): boolean {
  return user.role === "agent" && (!user.termsAcceptanceCurrent || !user.confidentialityAcceptanceCurrent)
}

/**
 * Agents with a missing or outdated terms or confidentiality acceptance
 * cannot reach any other page. Terms and privacy stay open so the
 * confirmations can link to them.
 */
export function AgentLegalGate({ children }: { children: ReactNode }) {
  const { isReady, hasToken } = useAuthSession()
  const pathname = usePathnameSafe()
  const queryClient = useQueryClient()
  const [holdingUserId, setHoldingUserId] = useState<string | null>(null)
  const profile = useQuery({
    queryKey: authKeys.user,
    queryFn: async () => {
      if (!authStorage.getToken()) return null
      try {
        return (await apiClient.users.getProfile()) as User
      } catch {
        return null
      }
    },
    enabled: isReady,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false,
  })
  const isPlayer = profile.data?.role === "player"
  const setup = useQuery({
    queryKey: playerSetupKey,
    queryFn: () => apiClient.playerSetup.get(),
    enabled: isReady && hasToken && isPlayer,
    retry: false,
  })

  if (!isReady || !hasToken) return children

  const user = profile.data
  if (profile.isPending && user == null) {
    return (
      <div className="flex min-h-dvh flex-col bg-background">
        <PageLoading pageTitle="Session" />
      </div>
    )
  }

  const readable = READABLE_WHILE_GATED.has(normalizePath(pathname))
  if (!user || readable) return children

  if (user.role === "player") {
    if (setup.isPending) {
      return (
        <div className="flex min-h-dvh flex-col bg-background">
          <PageLoading pageTitle="Onboarding" />
        </div>
      )
    }
    const detail = setup.data
    if (!detail) {
      return (
        <div className="flex min-h-dvh flex-col items-center justify-center gap-3 bg-background px-6 text-center">
          <p className="text-sm text-muted-foreground">Couldn't load your setup.</p>
          <button type="button" className="text-sm font-semibold text-primary" onClick={() => { void setup.refetch() }}>
            Try again
          </button>
        </div>
      )
    }
    if (!detail.started && holdingUserId !== user.id) {
      setHoldingUserId(user.id)
    }
    const showWizard = Boolean(detail && (!detail.started || holdingUserId === user.id))
    if (showWizard && detail) {
      return (
        <div className="min-h-dvh bg-background">
          <PlayerSetupWizard
            open
            setup={detail}
            onUpdated={(next) => {
              queryClient.setQueryData(playerSetupKey, next)
              if (next.termsCurrent) {
                queryClient.setQueryData(authKeys.user, (current: User | undefined) =>
                  current ? { ...current, termsAcceptanceCurrent: true } : current,
                )
              }
            }}
            onDismiss={() => setHoldingUserId(null)}
          />
        </div>
      )
    }
    const termsCurrent = user.termsAcceptanceCurrent || detail?.termsCurrent
    if (!termsCurrent) {
      return (
        <AgentTermsScreen
          key={`${user.id}:player-terms`}
          termsInitiallyAccepted={false}
          confidentialityInitiallyAccepted
          showConfidentiality={false}
          onAccepted={(next) => {
            queryClient.setQueryData(authKeys.user, next)
          }}
        />
      )
    }
    return children
  }

  if (!agentMustAccept(user)) return children

  return (
    <AgentTermsScreen
      key={`${user.id}:${user.termsAcceptanceCurrent}:${user.confidentialityAcceptanceCurrent}`}
      termsInitiallyAccepted={user.termsAcceptanceCurrent === true}
      confidentialityInitiallyAccepted={user.confidentialityAcceptanceCurrent === true}
      onAccepted={(next) => {
        queryClient.setQueryData(authKeys.user, next)
      }}
    />
  )
}
